// Global axios auth handling: keeps the user logged in for the token's full
// lifetime by refreshing it, and logs out only when the session is really gone.
//
// Rules:
// - "Token is Expired" (or a locally expired expiresAt) -> one shared refresh,
//   then retry the original request once.
// - "Token is Invalid" / "Authorization Token not found" / "Blacklisted", or a
//   refresh the server rejects -> log out.
// - 429, 5xx, network errors, timeouts, CORS -> never log out.
// - With no usable token, authenticated requests are cancelled before they are
//   sent: requests with a dead token count against the shared per-IP rate limit.
import axios from "axios";
import { REFRESH_TOKEN } from "../config/url";
import { getToken, isTokenExpired, saveSession, TOKEN_KEY } from "./session";
import { getRetryAfterSeconds } from "../utils/rateLimit";

// Refresh this long before expiresAt so an almost-expired token is never sent.
const EXPIRY_SKEW_MS = 30 * 1000;
// Back-off after a refresh fails for a transient reason (429, 5xx, offline).
const DEFAULT_REFRESH_BACKOFF_MS = 10 * 1000;

const LOGOUT_MESSAGES = [
  "token is invalid",
  "authorization token not found",
  "token is blacklisted",
];

// Bare instance, no interceptors, so refreshing can never recurse.
const refreshClient = axios.create();

let refreshPromise = null;
let refreshBlockedUntil = 0;

class SessionEndedError extends Error {}

function messageOf(error) {
  const message = error && error.response && error.response.data && error.response.data.message;
  return String(Array.isArray(message) ? message[0] : message || "").toLowerCase();
}

function bearer(token) {
  return `Bearer ${token}`;
}

function hasAuthHeader(config) {
  const value = config.headers && config.headers.Authorization;
  return typeof value === "string" && value.startsWith("Bearer");
}

function tokenOf(config) {
  return config.headers.Authorization.slice("Bearer ".length);
}

function cancel(reason) {
  return Promise.reject(new axios.Cancel(reason));
}

export function installAuthInterceptors(store, router) {
  function endSession() {
    if (store.getters["auth/getLoggedIn"] || getToken()) {
      store.dispatch("auth/logout");
    }
    const route = router.currentRoute.value;
    if (route && route.meta && route.meta.middleware) {
      router.push({ name: "login" });
    }
  }

  async function doRefresh() {
    const token = getToken();
    if (!token) throw new SessionEndedError("No token to refresh");

    let response;
    try {
      response = await refreshClient.get(REFRESH_TOKEN, {
        headers: { Authorization: bearer(token) },
        timeout: 20000,
      });
    } catch (error) {
      if (error.response && error.response.status === 401) {
        throw new SessionEndedError(messageOf(error) || "Refresh rejected");
      }
      // Server busy / rate limited / offline: keep the session, back off.
      const seconds = error.response ? getRetryAfterSeconds(error) : 0;
      refreshBlockedUntil = Date.now() + (seconds ? seconds * 1000 : DEFAULT_REFRESH_BACKOFF_MS);
      throw error;
    }

    const data = response.data || {};
    if (data.status != 1 || !data.api_token) {
      throw new SessionEndedError("Refresh rejected");
    }
    saveSession(data.api_token, data.expires_in);
    store.commit("auth/sessionRestored", data.api_token);
    return data.api_token;
  }

  // Single flight: concurrent callers share one in-flight refresh.
  function refreshSession() {
    if (!refreshPromise) {
      refreshPromise = doRefresh()
        .catch((error) => {
          if (error instanceof SessionEndedError) endSession();
          throw error;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
    return refreshPromise;
  }

  axios.interceptors.request.use(async (config) => {
    if (!hasAuthHeader(config)) return config;

    if (!getToken()) {
      return cancel("Not logged in; authenticated request not sent.");
    }

    if (refreshPromise || isTokenExpired(EXPIRY_SKEW_MS)) {
      if (!refreshPromise && Date.now() < refreshBlockedUntil) {
        return cancel("Session refresh is backing off; request not sent.");
      }
      try {
        await refreshSession();
      } catch (error) {
        return cancel("Session could not be refreshed; request not sent.");
      }
    }

    // Always send the latest token (it may have been refreshed here or in
    // another tab after this request's headers were built).
    config.headers.Authorization = bearer(getToken());
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      const status = error.response && error.response.status;
      if (status !== 401 || !config || !hasAuthHeader(config)) {
        return Promise.reject(error);
      }

      const message = messageOf(error);

      if (message.includes("token is expired")) {
        if (config._authRetried) return Promise.reject(error);
        config._authRetried = true;

        // Another tab (or a concurrent request) may already have a new token.
        const current = getToken();
        if (current && current !== tokenOf(config) && !isTokenExpired()) {
          config.headers.Authorization = bearer(current);
          return axios(config);
        }

        if (!refreshPromise && Date.now() < refreshBlockedUntil) {
          return Promise.reject(error);
        }
        try {
          const token = await refreshSession();
          config.headers.Authorization = bearer(token);
          return axios(config);
        } catch (refreshError) {
          return Promise.reject(error);
        }
      }

      if (LOGOUT_MESSAGES.some((text) => message.includes(text))) {
        endSession();
      }
      return Promise.reject(error);
    }
  );

  // Cross-tab sync: a login, refresh or logout in one tab applies to all.
  window.addEventListener("storage", (event) => {
    if (event.key !== TOKEN_KEY && event.key !== null) return;
    const token = getToken();
    if (token) {
      const wasLoggedIn = store.getters["auth/getLoggedIn"];
      store.commit("auth/sessionRestored", token);
      if (!wasLoggedIn) store.dispatch("auth/refreshProfile").catch(() => {});
    } else if (store.getters["auth/getLoggedIn"]) {
      endSession();
    }
  });

  // vuex-persistedstate may restore a stale loggedIn/access_token; the token
  // in localStorage wins.
  const token = getToken();
  if (token) {
    store.commit("auth/sessionRestored", token);
  } else if (store.getters["auth/getLoggedIn"]) {
    store.commit("auth/logout");
  }
}
